'use client';
import type { ReactNode } from 'react';
import ProductComparison from '@repo/ui/src/components/ProductComparison';
import type { Props as SectionProps } from '@repo/ui/src/components/ProductComparison/_Section';
import type { ImageProps, ProductProps } from '@repo/ui/src/types';
import type { ProductModelProps } from '@typings/product';
import Product from '@models/product';
import { useVipAccess } from '@hooks/customer';
import { logProduct } from '@utils/productWrapper';
import HelpIconTooltip from '@primitives/HelpIconTooltip';
import { warm } from '@style/variables';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

export interface ProductComparisonClientBoundaryProps {
  sections?: Array<SectionProps>;
  skus: Array<string>;
  _data: Array<ProductModelProps>;
  pageTitle: string;
  _testID: string;
  noSelectionImage: ImageProps;
  asContentRow?: boolean;
}

const ProductComparisonClientBoundary = ({
  pageTitle,
  sections,
  skus,
  _data,
  _testID,
  noSelectionImage,
  asContentRow,
}: ProductComparisonClientBoundaryProps) => {
  const { vip } = useVipAccess();
  const { locale } = useCurrentUrl();

  const rawProducts = _data.map((p) => new Product(p));
  const products = skus
    .map((sku) => rawProducts.find((p) => p.sku === sku))
    .filter((p): p is Product => p !== undefined);

  return (
    <ProductComparison
      pageTitle={pageTitle}
      sections={sections}
      products={products}
      _testID={_testID}
      noSelectionImage={noSelectionImage}
      asContentRow={asContentRow}
      vip={vip}
      renderTooltip={(text): ReactNode => (
        <HelpIconTooltip
          text={text}
          altColor={warm[900]}
          closeLabel="close information dialog"
          label="tooltip"
          border
        />
      )}
      onProductClick={(product, position) =>
        logProduct('PRODUCT_CLICKED', {
          product: product as unknown as ProductProps,
          list_name: 'Grill Comparison',
          locale,
          position,
        })
      }
    />
  );
};

export default ProductComparisonClientBoundary;
